// ─────────────────────────────────────────────────────────────────────────────
// Conjugarden sound — tiny Web Audio synth cues, no asset files. Lazily creates
// a single AudioContext (resumed on first user gesture) and plays short enveloped
// tones for correct/crit/miss/discover/victory/wither and a Trial countdown tick.
// Off by default-respecting a persisted preference; toggled from the run HUD.
// ─────────────────────────────────────────────────────────────────────────────
const PREF_KEY = 'c8-sound'

let ctx: AudioContext | null = null
let _enabled = true

function readPref(): boolean {
  if (typeof window === 'undefined') return true
  try { const v = window.localStorage.getItem(PREF_KEY); return v == null ? true : v === '1' } catch { return true }
}
_enabled = readPref()

function ac(): AudioContext | null {
  if (typeof window === 'undefined') return null
  try {
    if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    if (ctx.state === 'suspended') ctx.resume()
    return ctx
  } catch { return null }
}

// One enveloped oscillator note.
function note(freq: number, dur: number, opts: { type?: OscillatorType; gain?: number; delay?: number; glideTo?: number } = {}) {
  const c = ac()
  if (!c) return
  const t0 = c.currentTime + (opts.delay ?? 0)
  const osc = c.createOscillator()
  const g = c.createGain()
  osc.type = opts.type ?? 'triangle'
  osc.frequency.setValueAtTime(freq, t0)
  if (opts.glideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.glideTo), t0 + dur)
  const peak = opts.gain ?? 0.07
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  osc.connect(g).connect(c.destination)
  osc.start(t0)
  osc.stop(t0 + dur + 0.02)
}

const MAJOR = [0, 2, 4, 7, 9]          // pentatonic-ish, never sour
const semis = (base: number, s: number) => base * Math.pow(2, s / 12)

export const sound = {
  isEnabled: () => _enabled,
  setEnabled(v: boolean) {
    _enabled = v
    try { window.localStorage.setItem(PREF_KEY, v ? '1' : '0') } catch {}
    if (v) note(660, 0.12, { type: 'sine', gain: 0.05 })
  },

  // Correct answer — pitch climbs with the bloom streak.
  correct(combo: number) {
    if (!_enabled) return
    const step = MAJOR[Math.min(combo, 40) % MAJOR.length] + 12 * Math.floor(Math.min(combo, 40) / MAJOR.length)
    note(semis(523.25, step), 0.13, { type: 'triangle', gain: 0.06 })
  },
  crit() {
    if (!_enabled) return
    note(523.25, 0.16, { type: 'triangle', gain: 0.06 })
    note(783.99, 0.18, { type: 'triangle', gain: 0.05, delay: 0.04 })
    note(1046.5, 0.22, { type: 'sine', gain: 0.05, delay: 0.08 })
  },
  miss() {
    if (!_enabled) return
    note(180, 0.18, { type: 'sawtooth', gain: 0.05, glideTo: 90 })
  },
  discover(rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary') {
    if (!_enabled) return
    const tiers: Record<string, number[]> = {
      common: [0, 4], uncommon: [0, 4, 7], rare: [0, 4, 7, 11], epic: [0, 5, 9, 12], legendary: [0, 7, 12, 16, 19],
    }
    ;(tiers[rarity] ?? tiers.common).forEach((s, i) => note(semis(659.25, s), 0.22, { type: 'sine', gain: 0.05, delay: i * 0.06 }))
  },
  victory() {
    if (!_enabled) return
    ;[0, 4, 7, 12].forEach((s, i) => note(semis(523.25, s), 0.4, { type: 'triangle', gain: 0.06, delay: i * 0.1 }))
  },
  wither() {
    if (!_enabled) return
    ;[0, -3, -7, -12].forEach((s, i) => note(semis(392, s), 0.3, { type: 'sine', gain: 0.05, delay: i * 0.11 }))
  },
  tick() {
    if (!_enabled) return
    note(880, 0.05, { type: 'square', gain: 0.03 })
  },
  select() {
    if (!_enabled) return
    note(523.25, 0.07, { type: 'sine', gain: 0.04 })
  },
}
